import React, { useState } from 'react';
import { Slider, Input } from '@/ui/components';
import styles from './InstallmentAmountSelector.module.scss';
import { formatAmountWithSpacesAndSymbol } from '@/lib/amountUtils';
import { AMOUNT_SLIDER_STEP } from '@/features/offer';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';

export interface InstallmentAmountSelectorProps {
  creditAmount: number | undefined;
  onAmountChange: (amount: number) => void;
  className?: string;
  minAmount: number;
  maxAmount: number;
}

export const InstallmentAmountSelector: React.FC<
  InstallmentAmountSelectorProps
> = ({ creditAmount, onAmountChange, minAmount, maxAmount }) => {
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // A single possible amount: no slider, nothing to edit.
  const canChooseAmount = minAmount < maxAmount;

  const handleSliderChange = (value: number) => {
    onAmountChange(value);
  };

  const handleEditAmount = () => {
    setIsEditingAmount(true);
    setInputValue((creditAmount ?? 0).toString());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setInputValue(value);
  };

  const handleInputBlur = () => {
    const numericValue = parseInt(inputValue, 10) || 0;
    const clampedValue = Math.max(minAmount, Math.min(maxAmount, numericValue));
    onAmountChange(clampedValue);
    setIsEditingAmount(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  return (
    <div className={styles.amountCard}>
      {!isEditingAmount ? (
        <div className={styles.amountHeader}>
          <div className={styles.amountInfo}>
            <p className={styles.amountLabel}>Alege suma</p>
            <span className={styles.amountText}>
              {formatAmountWithSpacesAndSymbol(creditAmount, 'lei')}
            </span>
          </div>
          {canChooseAmount && (
            <div className={styles.editIcon}>
              <Button
                trackingId="edit_amount_installment_amount_selector"
                iconOnly
                size="xsmall"
                className={styles.editButton}
                variant="outlined"
                leftIcon={<Icon name="pen" className={styles.editIcon} />}
                onClick={handleEditAmount}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={styles.amountInputContainer}>
          <Input
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder="Introdu suma"
            autoFocus
            className={styles.amountInput}
          />
        </div>
      )}

      {/* Slider Section */}
      <Slider
        value={creditAmount ?? 0}
        min={minAmount}
        max={maxAmount}
        step={AMOUNT_SLIDER_STEP}
        onChange={handleSliderChange}
        formatValue={(value) => formatAmountWithSpacesAndSymbol(value, 'lei')}
        className={styles.slider}
      />
    </div>
  );
};
